import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FormField, Button } from '../components/ui';

const TRADES = [
  { value:'gas_engineer',    label:'Gas Engineer',    desc:'Gas Safety Certificates, boiler servicing', icon:'🔥' },
  { value:'electrician',     label:'Electrician',     desc:'EICR, rewiring, consumer units', icon:'⚡' },
  { value:'epc_assessor',    label:'EPC Assessor',    desc:'Energy Performance Certificates', icon:'📊' },
  { value:'plumber',         label:'Plumber',         desc:'Plumbing, Legionella checks', icon:'🚿' },
  { value:'general_builder', label:'General Builder', desc:'Maintenance and repairs', icon:'🏗' },
  { value:'locksmith',       label:'Locksmith',       desc:'Locks, security', icon:'🔑' },
  { value:'other',           label:'Other Trade',     desc:'Other services', icon:'🛠' },
];

const styles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #f5f2ec; font-family: 'DM Sans', sans-serif; }
  .cs-wrap { min-height: 100vh; display: flex; flex-direction: column; align-items: center; padding: 40px 20px; }
  .cs-card { background: #fff; border-radius: 16px; padding: 40px; width: 100%; max-width: 520px; box-shadow: 0 4px 24px rgba(0,0,0,.08); }
  .cs-logo { font-size: 22px; font-weight: 300; margin-bottom: 32px; text-align: center; }
  .cs-logo em { color: #1B4332; font-style: italic; }
  .cs-step { font-size: 12px; color: #6b7280; text-align: center; margin-bottom: 8px; text-transform: uppercase; letter-spacing: .05em; }
  .cs-title { font-size: 26px; font-weight: 300; margin-bottom: 8px; }
  .cs-sub { color: #6b7280; font-size: 14px; margin-bottom: 28px; line-height: 1.5; }
  .cs-trade-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; }
  .cs-trade { border: 1.5px solid #e5e7eb; border-radius: 10px; padding: 14px; cursor: pointer; transition: all .15s; }
  .cs-trade:hover { border-color: #1B4332; }
  .cs-trade.selected { border-color: #1B4332; background: #f0fdf4; }
  .cs-trade-icon { font-size: 24px; margin-bottom: 6px; }
  .cs-trade-label { font-size: 14px; font-weight: 600; }
  .cs-trade-desc { font-size: 12px; color: #6b7280; margin-top: 2px; }
  .cs-divider { border: none; border-top: 1px solid #e5e7eb; margin: 20px 0; }
  .cs-footer { text-align: center; margin-top: 20px; font-size: 13px; color: #6b7280; }
  .cs-footer a { color: #1B4332; }
`;

export default function ContractorSignup() {
  const [step, setStep]       = useState('email'); // email | otp | details | 
  const [email, setEmail]     = useState('');
  const [code, setCode]       = useState('');
  const [devCode, setDevCode] = useState('');
  const [form, setForm]       = useState({
    full_name:'', company_name:'', phone:'', postcode:'',
    coverage_radius_miles:25, bio:'', trade:'', accreditation:'', accred_number:'',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  async function requestOTP() {
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/contractor/request-otp', {
        method:'POST', headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send code');
      if (data.dev_code) setDevCode(data.dev_code);
      setStep('otp');
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }

  async function verifyOTP() {
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/contractor/verify-otp', {
        method:'POST', headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid code');
      if (!data.is_new) {
        // Existing contractor — redirect to dashboard
        localStorage.setItem('contractor_token', data.token);
        localStorage.setItem('contractor', JSON.stringify(data.contractor));
        window.location.href = '/contractor/dashboard';
        return;
      }
      setStep('details');
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }

  async function handleSignup() {
    if (!form.trade) { setError('Please select your trade'); return; }
    if (!form.full_name) { setError('Please enter your name'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/contractor/signup', {
        method:'POST', headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ ...form, email, coverage_radius_miles: Number(form.coverage_radius_miles) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Signup failed');
      window.location.href = data.checkout_url;
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="cs-wrap">
      <style>{styles}</style>

      <div className="cs-card">
        <div className="cs-logo">Landlord<em>Comply</em> — Contractor Portal</div>

        {step === 'email' && (<>
          <p className="cs-step">Step 1 of 3</p>
          <h1 className="cs-title">List your business</h1>
          <p className="cs-sub">Join the LandlordComply contractor network. Get leads from landlords who need certified professionals for gas safety, EICR, EPC and more.</p>

          <div style={{ background:'#f0fdf4', border:'1px solid #86efac', borderRadius:10, padding:'16px 20px', marginBottom:24 }}>
            <p style={{ fontWeight:600, color:'#15803d', marginBottom:4 }}>£9/month after 30-day free trial</p>
            <p style={{ fontSize:13, color:'#166534' }}>Cancel anytime. No setup fees. Card required to start trial.</p>
          </div>

          <FormField label="Your email address">
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@yourcompany.com" onKeyDown={e => e.key === 'Enter' && requestOTP()} />
          </FormField>

          {error && <p style={{ color:'#dc2626', fontSize:13, marginBottom:12 }}>{error}</p>}

          <Button loading={loading} onClick={requestOTP} disabled={!email} style={{ width:'100%', marginTop:8 }}>
            Continue →
          </Button>

          <div className="cs-footer">
            Already listed? <a href="/contractor/login">Sign in →</a>
          </div>
        </>)}

        {step === 'otp' && (<>
          <p className="cs-step">Step 1 of 3</p>
          <h1 className="cs-title">Check your email</h1>
          <p className="cs-sub">We sent a 6-digit code to <strong>{email}</strong>. Enter it below to verify your email.</p>

          {devCode && (
            <div style={{ background:'#fef9c3', border:'1px solid #fcd34d', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13 }}>
              Dev code: <strong>{devCode}</strong>
            </div>
          )}

          <FormField label="Verification code">
            <input value={code} onChange={e => setCode(e.target.value)} placeholder="123456"
              maxLength={6} style={{ letterSpacing:'0.2em', fontSize:24, textAlign:'center' }}
              onKeyDown={e => e.key === 'Enter' && verifyOTP()} />
          </FormField>

          {error && <p style={{ color:'#dc2626', fontSize:13, marginBottom:12 }}>{error}</p>}

          <Button loading={loading} onClick={verifyOTP} disabled={code.length !== 6} style={{ width:'100%', marginTop:8 }}>
            Verify →
          </Button>
          <p style={{ textAlign:'center', marginTop:12, fontSize:13 }}>
            <button onClick={() => { setStep('email'); setError(''); }} style={{ background:'none', border:'none', color:'#6b7280', cursor:'pointer' }}>
              ← Back
            </button>
          </p>
        </>)}

        {step === 'details' && (<>
          <p className="cs-step">Step 2 of 3</p>
          <h1 className="cs-title">Your profile</h1>
          <p className="cs-sub">This is what landlords will see when searching for contractors.</p>

          {/* Trade selection */}
          <p style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>Your main trade *</p>
          <div className="cs-trade-grid">
            {TRADES.map(t => (
              <div key={t.value} className={`cs-trade${form.trade === t.value ? ' selected' : ''}`}
                onClick={() => setForm(f => ({ ...f, trade: t.value }))}>
                <div className="cs-trade-icon">{t.icon}</div>
                <div className="cs-trade-label">{t.label}</div>
                <div className="cs-trade-desc">{t.desc}</div>
              </div>
            ))}
          </div>

          <hr className="cs-divider" />

          <FormField label="Your full name *">
            <input value={form.full_name} onChange={set('full_name')} placeholder="John Smith" />
          </FormField>

          <FormField label="Company name (optional)">
            <input value={form.company_name} onChange={set('company_name')} placeholder="Smith Gas Services Ltd" />
          </FormField>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <FormField label="Phone *">
              <input value={form.phone} onChange={set('phone')} placeholder="07700 900000" />
            </FormField>
            <FormField label="Base postcode *">
              <input value={form.postcode} onChange={set('postcode')} placeholder="SW1A 1AA" />
            </FormField>
          </div>

          <FormField label="Coverage radius (miles)">
            <select value={form.coverage_radius_miles} onChange={set('coverage_radius_miles')}>
              {[10,15,20,25,30,40,50].map(m => <option key={m} value={m}>{m} miles</option>)}
            </select>
          </FormField>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <FormField label="Accreditation (e.g. Gas Safe)">
              <input value={form.accreditation} onChange={set('accreditation')} placeholder="Gas Safe" />
            </FormField>
            <FormField label="Registration number">
              <input value={form.accred_number} onChange={set('accred_number')} placeholder="GS123456" />
            </FormField>
          </div>

          <FormField label="About you (optional)">
            <textarea value={form.bio} onChange={set('bio')} rows={3}
              placeholder="Years of experience, areas of specialisation, what makes you stand out…" />
          </FormField>

          {error && <p style={{ color:'#dc2626', fontSize:13, marginBottom:12 }}>{error}</p>}

          <Button loading={loading} onClick={handleSignup} style={{ width:'100%', marginTop:8 }}>
            Continue to payment →
          </Button>

          <p style={{ textAlign:'center', marginTop:12, fontSize:12, color:'#6b7280' }}>
            30-day free trial then £9/month. Cancel anytime.
          </p>
        </>)}
      </div>
    </div>
  );
}